import { getFullAddress } from "@/helper/format-data";
import {
  MAPBOX_ACCESS_TOKEN,
  MAPBOX_API,
  countrySymbol,
  MAPBOX_SEARCH_BOX_API,
  MAPBOX_REVERSE_GEOCODING_API,
} from "constants/mapbox.const";

import { Point } from "geojson";
import { getData } from "helper/api";
import { Address } from "types/address.type";

type SearchBoxCountry = {
  name: string;
  country_code: string;
  country_code_alpha_3: string;
};

type SearchBoxRegion = {
  name: string;
  region_code: string;
  region_code_full: string;
};

type SearchBoxPostcode = {
  name: string;
};

type SearchBoxPlace = {
  name: string;
};

type SearchBoxAddress = {
  name: string;
  address_number: string;
  street_name: string;
};
type SearchBoxStreet = {
  name: string;
};

type SearchBoxSuggestion = {
  feature_type: string;
  geometry: Point;
  properties: {
    context: {
      country?: SearchBoxCountry;
      region?: SearchBoxRegion;
      postcode?: SearchBoxPostcode;
      place?: SearchBoxPlace;
      address?: SearchBoxAddress;
      street?: SearchBoxStreet;
    };
  };
  id: string;
};
type SearchBoxAPiResponse = {
  features: SearchBoxSuggestion[];
};

export type PlacesByTextItem = Address & { geometry: Point; value: string; label: string };
export type PlacesByTextResponse = PlacesByTextItem[];

type MapboxFeature = {
  id: string;
  text: string;
  address?: string;
  place_name: string;
  center: [number, number];
  properties: {
    wikidata?: string;
  };
};

type MapboxResponse = {
  features: MapboxFeature[];
};

export const getAddress = async (point: Point, geocodingTypes?: string[]): Promise<AddressType> => {
  const placeName = encodeURIComponent(`${point.coordinates[0]},${point.coordinates[1]}.json`);
  const types = geocodingTypes ? `&types=${geocodingTypes.join(",")}` : "";
  const mapboxRequestUrl = `${MAPBOX_API}${placeName}?access_token=${MAPBOX_ACCESS_TOKEN}${types}`;

  try {
    const mapboxResponse = await getData<MapboxResponse>(mapboxRequestUrl);
    const address: AddressType = {};
    for (const mapboxFeature of mapboxResponse.features) {
      if (mapboxFeature.id.includes("address.")) {
        address.name = mapboxFeature.text;
        address.number = mapboxFeature.address;
      } else if (mapboxFeature.id.includes("postcode.")) {
        address.postcode = mapboxFeature.text;
      } else if (mapboxFeature.id.includes("region.")) {
        // Need to confirm
        address.province = mapboxFeature.text;
      } else if (mapboxFeature.id.includes("place.")) {
        address.city = mapboxFeature.text;
      } else if (mapboxFeature.id.includes("country.")) {
        address.country = mapboxFeature.text;
      }
    }

    return address;
  } catch {
    return {};
  }
};

export const getPlacesByTextV5 = async ({ keyWord, coordinates }: placeFilter): Promise<addressPosition[]> => {
  const placeName = encodeURIComponent(`${keyWord}.json`);
  const proximity = coordinates ? `proximity=${coordinates?.longitude},${coordinates?.latitude}&` : "";
  const mapboxRequestUrl = `${MAPBOX_API}${placeName}?language=${countrySymbol}&${proximity}access_token=${MAPBOX_ACCESS_TOKEN}`;
  try {
    const mapboxResponse = await getData<MapboxResponse>(mapboxRequestUrl);
    const address: Array<addressPosition> = [];
    for (const mapboxFeature of mapboxResponse.features) {
      if (
        mapboxFeature.id.includes("address.") ||
        mapboxFeature.id.includes("place.") ||
        mapboxFeature.id.includes("poi.")
      ) {
        address.push({
          name: mapboxFeature.place_name,
          latitude: mapboxFeature.center[0],
          longitude: mapboxFeature.center[1],
          wikidata: mapboxFeature.properties.wikidata,
          text: mapboxFeature.text,
        });
      }
    }
    return address;
  } catch {
    return [];
  }
};

export const typeSearch = "address%2Cstreet%2Cplace";

export const getAddressByPoint = async (point: Point, geocodingTypes?: string[]): Promise<PlacesByTextItem> => {
  const placeName = `longitude=${point.coordinates[0]}&latitude=${point.coordinates[1]}`;
  const types = geocodingTypes ? `&types=${geocodingTypes.join(",")}` : "&types=address";
  const mapboxRequestUrl = `${MAPBOX_REVERSE_GEOCODING_API}${placeName}&access_token=${MAPBOX_ACCESS_TOKEN}${types}`;

  try {
    const mapboxResponse = await getData<SearchBoxAPiResponse>(mapboxRequestUrl);
    return getAddressByContext(mapboxResponse.features[0]);
  } catch {
    return {
      streetName: "",
      streetNumber: "",
      city: "",
      postalCode: "",
      country: "",
      label: "",
      value: "",
      geometry: point,
    };
  }
};

export const getPlacesByText = async ({ keyWord, coordinates }: placeFilter): Promise<PlacesByTextResponse> => {
  const placeName = "q=" + encodeURIComponent(`${keyWord}`);
  const proximity = coordinates ? `proximity=${coordinates?.longitude},${coordinates?.latitude}&` : "";
  const mapboxRequestUrl = `${MAPBOX_SEARCH_BOX_API}${placeName}&language=${countrySymbol}&${proximity}access_token=${MAPBOX_ACCESS_TOKEN}&types=${typeSearch}`;
  try {
    const mapboxResponse = await getData<SearchBoxAPiResponse>(mapboxRequestUrl);
    const address: Array<PlacesByTextItem> = [];
    for (const mapboxFeature of mapboxResponse.features) {
      const newAddress = getAddressByContext(mapboxFeature);
      address.push(newAddress);
    }
    return address;
  } catch {
    return [];
  }
};

export type addressPosition = {
  name: string;
  latitude: number;
  longitude: number;
  wikidata?: string;
  text?: string;
};

export type AddressType = {
  name?: string;
  number?: string;
  postcode?: string;
  city?: string;
  country?: string;
  province?: string;
};

export type placeFilter = {
  coordinates?: Omit<addressPosition, "name">;
  keyWord: string;
};

export const getAddressByContext = (feature: SearchBoxSuggestion): PlacesByTextItem => {
  const { properties, geometry, id } = feature;
  const streetName = properties.context.address?.street_name || properties.context.street?.name;
  const newAddress = {
    streetNumber: properties.context.address?.address_number ?? "",
    streetName: streetName ?? "",
    postalCode: properties.context.postcode?.name ?? "",
    province: properties.context.region?.name ?? "",
    city: properties.context.place?.name ?? "",
    country: properties.context.country?.name ?? "",
    geometry,
    value: id,
  };
  const fullAddress = getFullAddress(newAddress);
  return { ...newAddress, label: fullAddress };
};
